
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface Application {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  portfolio_links: string[] | null;
  application_reason: string | null;
  rejection_reason: string | null;
  admin_notes: string | null;
  applied_at: string;
  reviewed_at: string | null;
}

interface ActivityLogEntry {
  id: string;
  action: string;
  previous_status: string | null;
  new_status: string | null;
  reason: string | null;
  created_at: string;
  admin_user_id: string | null;
  admin_username?: string;
}

const statusConfig: Record<string, { label: string; icon: string; color: string; bg: string; border: string; ring: string; description: string }> = {
  pending: {
    label: 'Under Review',
    icon: 'ri-time-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    ring: 'ring-amber-400',
    description: 'Your application is being reviewed by our team. This usually takes 3-5 business days.',
  },
  approved: {
    label: 'Approved',
    icon: 'ri-check-double-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    ring: 'ring-emerald-400',
    description: 'Congratulations! Your application has been approved. You are now a verified AI Drama Creator.',
  },
  rejected: {
    label: 'Not Approved',
    icon: 'ri-close-circle-line',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    ring: 'ring-red-400',
    description: 'Unfortunately, your application was not approved at this time. You may reapply in the future.',
  },
  suspended: {
    label: 'Suspended',
    icon: 'ri-pause-circle-line',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    ring: 'ring-orange-400',
    description: 'Your creator status has been suspended. Please contact support for more information.',
  },
};

const steps = [
  { key: 'submitted', label: 'Submitted', icon: 'ri-send-plane-line' },
  { key: 'review', label: 'In Review', icon: 'ri-search-eye-line' },
  { key: 'decision', label: 'Decision', icon: 'ri-shield-check-line' },
];

interface Props {
  userId: string;
  onApplyAgain: () => void;
}

const ApplicationStatusTracker = ({ userId, onApplyAgain }: Props) => {
  const [application, setApplication] = useState<Application | null>(null);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTimeline, setShowTimeline] = useState(false);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    fetchApplication();
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [userId]);

  const fetchApplication = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('creator_applications')
        .select('*')
        .eq('user_id', userId)
        .order('applied_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setApplication(data);
        fetchActivityLog(data.id);
        setupRealtime(data.id);
      }
    } catch (err) {
      console.error('Error fetching application:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLog = async (applicationId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_activity_log')
        .select('*')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const adminIds = [...new Set(data.filter(d => d.admin_user_id).map(d => d.admin_user_id))];
        let adminMap: Record<string, string> = {};
        if (adminIds.length > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, username')
            .in('id', adminIds);
          if (profiles) {
            adminMap = Object.fromEntries(profiles.map(p => [p.id, p.username || 'Admin']));
          }
        }
        setActivityLog(data.map(entry => ({
          ...entry,
          admin_username: entry.admin_user_id ? (adminMap[entry.admin_user_id] || 'Admin') : 'System',
        })));
      }
    } catch (err) {
      console.error('Error fetching activity log:', err);
    }
  };

  const setupRealtime = (applicationId: string) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(`app-status-${applicationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'creator_applications',
          filter: `id=eq.${applicationId}`,
        },
        (payload) => {
          const updated = payload.new as Application;
          setApplication(prev => prev ? { ...prev, ...updated } : null);
          fetchActivityLog(applicationId);
        }
      )
      .subscribe();

    channelRef.current = channel;
  };

  const getStepStatus = (stepKey: string, appStatus: string) => {
    if (stepKey === 'submitted') return 'completed';
    if (stepKey === 'review') {
      if (appStatus === 'pending') return 'active';
      return 'completed';
    }
    if (stepKey === 'decision') {
      if (appStatus === 'pending') return 'upcoming';
      return 'completed';
    }
    return 'upcoming';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeSince = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, { label: string; icon: string; color: string }> = {
      approved: { label: 'Application Approved', icon: 'ri-check-double-line', color: 'text-emerald-600' },
      rejected: { label: 'Application Rejected', icon: 'ri-close-circle-line', color: 'text-red-600' },
      suspended: { label: 'Creator Suspended', icon: 'ri-pause-circle-line', color: 'text-orange-600' },
      reactivated: { label: 'Application Reactivated', icon: 'ri-restart-line', color: 'text-teal-600' },
      info_requested: { label: 'More Info Requested', icon: 'ri-question-line', color: 'text-sky-600' },
      submitted: { label: 'Application Submitted', icon: 'ri-send-plane-line', color: 'text-amber-600' },
    };
    return labels[action] || { label: action, icon: 'ri-information-line', color: 'text-gray-600' };
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-100 rounded-xl"></div>
      </div>
    );
  }

  if (!application) return null;

  const config = statusConfig[application.status] || statusConfig.pending;

  return (
    <div className="space-y-4">
      {/* Main Status Card */}
      <div className={`${config.bg} ${config.border} border rounded-xl overflow-hidden`}>
        {/* Status Header */}
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${config.bg} border ${config.border} flex items-center justify-center`}>
              <i className={`${config.icon} text-xl ${config.color}`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className={`font-bold ${config.color}`}>{config.label}</h4>
                {application.status === 'pending' && (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Applied {formatDate(application.applied_at)}
              </p>
            </div>
          </div>
          {application.reviewed_at && (
            <div className="text-right">
              <p className="text-[11px] text-gray-400 uppercase tracking-wider font-medium">Reviewed</p>
              <p className="text-xs text-gray-600 font-medium">{getTimeSince(application.reviewed_at)}</p>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="px-5 pb-4">
          <div className="flex items-center">
            {steps.map((step, idx) => {
              const stepStatus = getStepStatus(step.key, application.status);
              const isLast = idx === steps.length - 1;
              return (
                <div key={step.key} className={`flex items-center ${isLast ? '' : 'flex-1'}`}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                        stepStatus === 'completed'
                          ? application.status === 'rejected' && step.key === 'decision'
                            ? 'bg-red-500 text-white'
                            : application.status === 'suspended' && step.key === 'decision'
                            ? 'bg-orange-500 text-white'
                            : 'bg-emerald-500 text-white'
                          : stepStatus === 'active'
                          ? `bg-white border-2 ${config.border} ${config.color} ring-4 ${config.ring}/20`
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {stepStatus === 'completed' ? (
                        step.key === 'decision' && (application.status === 'rejected' || application.status === 'suspended') ? (
                          <i className={`${application.status === 'rejected' ? 'ri-close-line' : 'ri-pause-line'} text-sm`}></i>
                        ) : (
                          <i className="ri-check-line text-sm"></i>
                        )
                      ) : (
                        <i className={`${step.icon} text-sm`}></i>
                      )}
                    </div>
                    <span className={`text-[11px] mt-1.5 font-medium ${
                      stepStatus === 'active' ? config.color : stepStatus === 'completed' ? 'text-gray-700' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {!isLast && (
                    <div className={`flex-1 h-0.5 mx-2 mb-5 rounded-full transition-all ${
                      stepStatus === 'completed' || getStepStatus(steps[idx + 1].key, application.status) !== 'upcoming'
                        ? application.status === 'rejected' ? 'bg-red-300' : application.status === 'suspended' ? 'bg-orange-300' : 'bg-emerald-300'
                        : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Description */}
        <div className="px-5 pb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{config.description}</p>
        </div>

        {/* Rejection Reason */}
        {application.rejection_reason && application.status === 'rejected' && (
          <div className="mx-5 mb-4 bg-white/60 rounded-lg p-3 border border-red-100">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-red-500 mt-0.5"></i>
              <div>
                <p className="text-xs font-semibold text-red-700 mb-1">Reason</p>
                <p className="text-sm text-red-600">{application.rejection_reason}</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Notes */}
        {application.admin_notes && (
          <div className="mx-5 mb-4 bg-white/60 rounded-lg p-3 border border-amber-100">
            <div className="flex items-start gap-2">
              <i className="ri-sticky-note-line text-amber-500 mt-0.5"></i>
              <div>
                <p className="text-xs font-semibold text-amber-700 mb-1">Admin Note</p>
                <p className="text-sm text-amber-700 whitespace-pre-wrap">{application.admin_notes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-5 pb-4 flex items-center gap-3">
          {(application.status === 'rejected' || application.status === 'suspended') && (
            <button
              onClick={onApplyAgain}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-sm font-medium rounded-lg hover:from-orange-600 hover:to-rose-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-refresh-line"></i>
              Apply Again
            </button>
          )}
          {activityLog.length > 0 && (
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="px-4 py-2 bg-white/80 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-white transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className={`ri-history-line ${showTimeline ? 'text-amber-500' : ''}`}></i>
              {showTimeline ? 'Hide' : 'View'} Activity Log
            </button>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      {showTimeline && activityLog.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-history-line text-amber-500"></i>
            <h5 className="text-sm font-semibold text-gray-900">Activity Timeline</h5>
            <span className="text-xs text-gray-400 ml-auto">{activityLog.length} event{activityLog.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="p-5">
            <div className="relative">
              <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200"></div>
              <div className="space-y-5">
                {activityLog.map((entry) => {
                  const actionInfo = getActionLabel(entry.action);
                  return (
                    <div key={entry.id} className="flex gap-4 relative">
                      <div className={`w-8 h-8 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center flex-shrink-0 z-10`}>
                        <i className={`${actionInfo.icon} text-xs ${actionInfo.color}`}></i>
                      </div>
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-medium ${actionInfo.color}`}>{actionInfo.label}</span>
                          {entry.previous_status && entry.new_status && (
                            <span className="text-[11px] text-gray-400 flex items-center gap-1">
                              <span className="capitalize">{entry.previous_status}</span>
                              <i className="ri-arrow-right-s-line text-[10px]"></i>
                              <span className="capitalize">{entry.new_status}</span>
                            </span>
                          )}
                        </div>
                        {entry.reason && (
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{entry.reason}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <i className="ri-time-line text-[10px]"></i>
                            {formatDate(entry.created_at)}
                          </span>
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <i className="ri-user-line text-[10px]"></i>
                            {entry.admin_username}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStatusTracker;
